"use client";

import React, { useState } from "react";
import { ChevronsLeftRight, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";

// 定義 Props 介面
interface ColorMatchingToolCtaProps {
  beforeImage?: string;
  afterImage?: string;
}
export default function ColorMatchingToolCta({
  beforeImage = "https://hent.services/wp-content/uploads/2026/05/Before_No_Floor.webp",
  afterImage = "https://hent.services/wp-content/uploads/2026/05/After_Floor_HD0036.webp"
}: ColorMatchingToolCtaProps) {
  const [sliderPosition, setSliderPosition] = useState(50);
  const handleSliderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSliderPosition(Number(event.target.value));
  };
  return <section id="線上配色套圖-color-matching-tool-cta" className="dark bg-background text-foreground w-full overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-[600px]">
        {/* 左側對比圖區 */}
        <div className="relative w-full h-[400px] lg:h-auto select-none touch-none group">
          {/* 施工後圖片 */}
          <img src={afterImage} alt="Flooring after" className="absolute inset-0 w-full h-full object-cover" />

          {/* 施工前圖片 (使用 clipPath 裁切) */}
          <div className="absolute inset-0 w-full h-full overflow-hidden" style={{
          clipPath: `polygon(0 0, ${sliderPosition}% 0, ${sliderPosition}% 100%, 0 100%)`
        }}>
            <img src={beforeImage} alt="Flooring before" className="absolute inset-0 w-full h-full object-cover" />
          </div>

          {/* Slider 控制滑桿 */}
          <input type="range" min="0" max="100" value={sliderPosition} onChange={handleSliderChange} className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize z-20" />

          {/* 分隔線 UI */}
          <div className="absolute top-0 bottom-0 w-1 bg-white/80 cursor-ew-resize pointer-events-none z-10 flex items-center justify-center shadow-[0_0_10px_rgba(0,0,0,0.5)]" style={{
          left: `${sliderPosition}%`,
          transform: "translateX(-50%)"
        }}>
            <div className="w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center text-primary ring-4 ring-primary/10 group-hover:scale-110 transition-transform">
              <ChevronsLeftRight className="w-6 h-6" />
            </div>
          </div>

          <div className="absolute top-6 left-6 z-10 bg-black/60 backdrop-blur-sm text-white px-3 py-1 text-xs tracking-widest uppercase rounded-sm">施工前 / BEFORE</div>
          <div className="absolute top-6 right-6 z-10 bg-black/60 backdrop-blur-sm text-white px-3 py-1 text-xs tracking-widest uppercase rounded-sm">施工後 / AFTER</div>
        </div>

        {/* ==========================================
            右側文字內容區 ( 完美導入不消失 img 背景方案)
           ========================================== */}
        <div className="relative flex flex-col justify-center min-h-[350px] md:min-h-[450px] p-8 md:p-16 lg:p-24 text-white overflow-hidden">
          {/*  核心背景圖標籤：完美取代 CSS 背景圖，防止圖片消失且加載更快 */}
          <img src="https://hent.services/wp-content/uploads/2026/05/Floor_Match_BG-scaled.webp" alt="Floor Match Background" className="absolute inset-0 w-full h-full object-cover object-center z-0 pointer-events-none" />
          
          {/* 真正文字內容外層：拉高 z-index 確保蓋在背景圖之上且按鈕可正常點擊 */}
          <div className="relative z-10 max-w-xl space-y-8">
            <div className="space-y-3 select-none">
              {/* 標題區塊微調：Noto Sans HK優雅細體 */}
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-hk-regular font-normal text-[#1a1a1a] tracking-[0.08em] leading-tight">
                線上配色體驗
              </h2>
              
              {/* 核心修改：極簡人文橫線 */}
              <div className="pt-1">
                <div className="w-48 h-[2px] bg-[#7a6a59]/60" />
              </div>
            </div>

            {/* 描述文字區塊：深炭灰高混濁度變體、字距拉開 */}
            <div className="space-y-4 text-[#1a1a1a]/80 text-base md:text-lg leading-relaxed font-hk-regular tracking-wider max-w-xl">
              <p>以更直覺的方式，想像未來的居所。</p>
              <p>
                透過線上配色模擬，上傳您的空間影像或選擇情境範例，
                即可即時套用 Hent’s 木地板系列，細緻感受色調、紋理與空間氛圍的自然融合。
              </p>
            </div>

            {/* 按鈕點擊區 */}
            <div className="pt-4">
              <Button size="lg" className="h-14 px-10 text-lg rounded-sm bg-primary hover:bg-primary/90 text-white transition-all shadow-lg" asChild>
                <a href="https://pim-client.wizart.ai/fitting-room?api_token=3DbIXLfXHxV5t845ztHFBmsXU92FacZQAwh5pKO4axfHSXDef6365nmWv129&bba=true" target="_blank" rel="noopener noreferrer" className="flex items-center text-white">
                  立即體驗
                  <ArrowRight className="ml-3 w-5 h-5" />
                </a>
              </Button>
            </div>
          </div>
        </div>

      </div>
    </section>;
}